import { createClient } from 'matrix-js-sdk/src';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useToast from 'contexts/ToastProvider/useToast';
import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import { getSessionStorageValue, removeSessionStorageValue, SessionStateKeys } from 'hooks/useSessionState';
import useLogout from 'modules/Authentication/hooks/useLogout';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import { logAuthLifecycle, sanitizeMatrixError } from 'modules/Authentication/sessionObservability';
import {
  generateSearchSecret,
  resetMatrixRefreshCircuitBreaker,
} from 'modules/Authentication/utils/matrixTokenRefresh';
import { storageUtils } from 'modules/CalendarView/CalendarSync/CalendarSyncModal/utils';
import { getHomeserverBaseUrl } from 'modules/Matrix/utils';
import constants from 'utils/Constants';
import getDeviceLabel from 'utils/getDeviceLabel';
import { authLogger } from 'utils/logging/scopedLogger';
import { meedioMatrixFetchFn } from 'utils/matrixClientFetch';

import useMatrixContext from './useMatrixContext';
import useStartMatrixClient from './useStartMatrixClient';
import { MatrixLoggingInStatusEnum } from './utils';

/**
 * Hook for handling Matrix authentication
 *
 * Provides functionality for initiating Matrix SSO redirects and token-based logins
 *
 * @returns initiateTokenLogin - For initiating Matrix token-based logins
 */
const useMatrixAuthentication = () => {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const logoutGuestSession = useLogout();
  const { mxDeviceId } = useMxCookies();
  const matrixContextValue = useMatrixContext({ requireDefinedMatrixClient: false });
  const startMatrixClient = useStartMatrixClient(matrixContextValue);
  const [workspaceInviteToken, setWorkspaceInviteToken] = useLocalStorage(constants.WORKSPACE_INVITE_TOKEN, '');

  const {
    setMatrixLoggingInStatus,
    setMatrixHomeserverBaseUrl,
    setMatrixUrl,
    loginUrl,
    clearLoginUrl,
    setMxAvatarUrl,
    matrixClientRef,
  } = matrixContextValue;

  /**
   * Initiate Matrix token-based login, validated loginToken and starts new matrix client with matrix credentials
   * @param loginToken - The login token to use
   * @returns new Matrix client
   */
  const initiateTokenLogin = useCallback(
    async (loginToken: string) => {
      setMatrixLoggingInStatus(MatrixLoggingInStatusEnum.RUNNING);
      const baseUrl = await getHomeserverBaseUrl(loginUrl);
      if (!baseUrl) throw new Error(`Failed to get homeserver base url for ${loginUrl}`);

      setMatrixUrl(loginUrl);
      setMatrixHomeserverBaseUrl(baseUrl);
      authLogger.info('Matrix homeserver configured for token login', { homeserver_configured: true });
      try {
        const authClient = createClient({ baseUrl, fetchFn: meedioMatrixFetchFn });
        const params = new URLSearchParams(searchParams);

        // NOTE: removing loginToken from search params to avoid it being used again (like refresh)
        params.delete('loginToken');
        setSearchParams(params);

        const isSoftLogoutRecovery =
          getSessionStorageValue<boolean>(SessionStateKeys.MATRIX_SOFT_LOGOUT_RECOVERY) === true;

        authLogger.info('Sending token login request');
        logAuthLifecycle('Token login started', {
          domain: 'auth',
          action: 'token_login',
          phase: 'start',
          mx_device_id: mxDeviceId,
        });
        resetMatrixRefreshCircuitBreaker();
        const response = await authClient.loginRequest({
          type: 'm.login.token',
          token: loginToken,
          refresh_token: true,
          initial_device_display_name: getDeviceLabel(),
          ...(isSoftLogoutRecovery && mxDeviceId ? { device_id: mxDeviceId } : {}),
        });

        if (!response.refresh_token) {
          logAuthLifecycle(
            'Matrix login did not return refresh_token',
            {
              domain: 'auth',
              action: 'token_login',
              outcome: 'warn',
              has_refresh_token: false,
              mx_device_id: response.device_id,
            },
            'warn'
          );
        }

        if (isSoftLogoutRecovery) {
          removeSessionStorageValue(SessionStateKeys.MATRIX_SOFT_LOGOUT_RECOVERY);
        }

        //Clear calendar tokens after log in
        storageUtils.removeTokens();

        if (matrixClientRef.current) {
          setMxAvatarUrl('');
          authLogger.info('Sending guest logout request without reload');
          await logoutGuestSession(matrixClientRef.current, false);
        }

        const searchSecret = generateSearchSecret();

        authLogger.info('Start matrix client');
        logAuthLifecycle('Token login succeeded — starting matrix client', {
          domain: 'auth',
          action: 'token_login',
          outcome: 'success',
          mx_device_id: response.device_id,
          has_refresh_token: !!response.refresh_token,
        });
        const newMatrixClient = await startMatrixClient({
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          accessTokenExpiresAt: response.expires_in_ms
            ? new Date(Date.now() + response.expires_in_ms).toISOString()
            : undefined,
          searchSecret,
          deviceId: response.device_id,
          userId: response.user_id,
          tokenLogin: true,
          shouldPreserveStores: isSoftLogoutRecovery,
          customUrl: baseUrl,
        });

        // startMatrixClient resolves `undefined` when the client could not start (e.g. the user
        // cancelled the secret-storage unlock gate, or crypto init failed irrecoverably). Leaving the
        // status at RUNNING with no client strands the user on an infinite LoadingScreen (InitializeMatrix
        // treats FINISHED-without-client as loading too). Exit to /auth so the user can retry — the
        // unlock gate keeps RUNNING while it is open, so this only fires on a genuine non-start.
        if (!newMatrixClient) {
          logAuthLifecycle(
            'Token login did not start a client — redirecting to auth',
            {
              domain: 'auth',
              action: 'token_login',
              outcome: 'fail',
              mx_device_id: mxDeviceId,
            },
            'warn'
          );
          setMatrixLoggingInStatus(MatrixLoggingInStatusEnum.FINISHED);
          navigate('/auth', { replace: true });
          return;
        }

        setMatrixLoggingInStatus(MatrixLoggingInStatusEnum.FINISHED);
        removeSessionStorageValue(SessionStateKeys.AUTH_FAILURE_LOGOUT_TOAST_SHOWN);
        // SSO recovery succeeded — clear the stale logged-out flag so useSessionLoggedOut
        // does not show the "couldn't access session data" toast after a successful login.
        removeSessionStorageValue(SessionStateKeys.IS_SESSION_LOGGED_OUT);
        clearLoginUrl();

        if (workspaceInviteToken) {
          setWorkspaceInviteToken('');
          navigate(`/workspace/${workspaceInviteToken}`);
        }

        return newMatrixClient;
      } catch (e) {
        const sanitized = sanitizeMatrixError(e);
        logAuthLifecycle(
          'Token login failed',
          {
            domain: 'auth',
            action: 'token_login',
            outcome: 'fail',
            mx_device_id: mxDeviceId,
          },
          'warn',
          sanitized
        );
        pushToast({ title: t('sso_login_failed'), variant: 'error' });
        setMatrixLoggingInStatus(MatrixLoggingInStatusEnum.FINISHED);
        navigate('/auth');
      }
    },
    [
      clearLoginUrl,
      loginUrl,
      logoutGuestSession,
      matrixClientRef,
      mxDeviceId,
      navigate,
      pushToast,
      searchParams,
      setMatrixHomeserverBaseUrl,
      setMatrixLoggingInStatus,
      setMatrixUrl,
      setMxAvatarUrl,
      setSearchParams,
      startMatrixClient,
      t,
      workspaceInviteToken,
      setWorkspaceInviteToken,
    ]
  );

  return { initiateTokenLogin };
};

export default useMatrixAuthentication;
